import { TypeKeys, generateActionId } from './actionsUtils.js';
import { convertToCommonErrors } from '../../common/utils/errorUtils.js';
import type { Action } from 'redux';
import type { CommonError } from '../../common/types/errors.js';
import type { CrudAction, ErrorAction } from './actionsUtils.js';
import type {
  GenericError,
  OnlineOrderReview,
  PostReviewRequest,
  SubscriptionAction,
  SubscriptionRedeemReview,
  SubscriptionsResponse,
} from '../../generated/api/models.js';
import type { SubscriptionCategory } from '../../common/enums.js';

// get user Subscriptions
export interface GetEmployeeSubscriptionsAction extends CrudAction<TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS> {
  subscriptionItemId?: string;
  subscriptionType: SubscriptionCategory;
}

export interface GetEmployeeSubscriptionsFulfilledAction extends Action {
  response: SubscriptionsResponse;
  subscriptionType: SubscriptionCategory;
  type: TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FULLFILLED;
  subscriptionItemId?: string;
}

export type GetEmployeeSubscriptionsFailedAction = ErrorAction<TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FAILED>;

export const getEmployeeSubscriptions = (
  subscriptionType: SubscriptionCategory,
  subscriptionItemId?: string
): GetEmployeeSubscriptionsAction => ({
  id: generateActionId(),
  subscriptionItemId,
  subscriptionType,
  type: TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS,
});

export const getEmployeeSubscriptionsFulfilled = (
  response: SubscriptionsResponse,
  subscriptionType: SubscriptionCategory,
  subscriptionItemId?: string
): GetEmployeeSubscriptionsFulfilledAction => ({
  response,
  subscriptionItemId,
  subscriptionType,
  type: TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FULLFILLED,
});

export const getEmployeeSubscriptionsFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): GetEmployeeSubscriptionsFailedAction => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FAILED,
});

export interface CheckValidEmployeeSubscription extends CrudAction<TypeKeys.CHECK_VALID_EMPLOYEE_SUBSCRIPTION> {
  subscriptionType: string;
  subscriptionToValidate?: string;
}

export const checkValidEmployeeSubscription = (
  subscriptionType: SubscriptionCategory,
  subscriptionToValidate?: string
): CheckValidEmployeeSubscription => ({
  id: generateActionId(),
  subscriptionToValidate,
  subscriptionType,
  type: TypeKeys.CHECK_VALID_EMPLOYEE_SUBSCRIPTION,
});

export interface ReviewAction extends CrudAction<TypeKeys.REVIEW> {
  request: OnlineOrderReview | SubscriptionRedeemReview;
  reviewType: PostReviewRequest.ReviewTypeEnum;
  approve: boolean;
  rejectReason?: string;
  validationErrors?: CommonError[];
  customerOrderDisplayId?: string;
  pendingSubscriptionActions?: SubscriptionAction[];
}

export interface ReviewFulfilledAction {
  reviewType: PostReviewRequest.ReviewTypeEnum;
  approved: boolean;
  subscriptionId?: string;
  customerOrderDisplayId?: string;
  pendingSubscriptionActions?: SubscriptionAction[];
  type: TypeKeys.REVIEW_FULFILLED;
}

export type ReviewFailedAction = ErrorAction<TypeKeys.REVIEW_FAILED>;

export const review = (
  request: OnlineOrderReview | SubscriptionRedeemReview,
  reviewType: PostReviewRequest.ReviewTypeEnum,
  approve: boolean,
  rejectReason?: string,
  validationErrors?: CommonError[],
  customerOrderDisplayId?: string,
  pendingSubscriptionActions?: SubscriptionAction[]
): ReviewAction => ({
  request,
  reviewType,
  approve,
  rejectReason,
  validationErrors,
  customerOrderDisplayId,
  pendingSubscriptionActions,
  id: generateActionId(),
  type: TypeKeys.REVIEW,
});

export const reviewFulfilled = (
  reviewType: PostReviewRequest.ReviewTypeEnum,
  approved: boolean,
  subscriptionId?: string,
  customerOrderDisplayId?: string,
  pendingSubscriptionActions?: SubscriptionAction[]
): ReviewFulfilledAction => ({
  reviewType,
  approved,
  subscriptionId,
  customerOrderDisplayId,
  pendingSubscriptionActions,
  type: TypeKeys.REVIEW_FULFILLED,
});

export const reviewFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): ReviewFailedAction => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.REVIEW_FAILED,
});

export type UserActionTypes =
  | ReviewAction
  | ReviewFailedAction
  | ReviewFulfilledAction
  | GetEmployeeSubscriptionsAction
  | GetEmployeeSubscriptionsFulfilledAction
  | GetEmployeeSubscriptionsFailedAction
  | CheckValidEmployeeSubscription;
